import type { ReactNode } from "react";
import { TagOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../store/hooks";
import ProfileDropdown from "../../../components/ProfileDropdown";
import BackButton from "../../../components/BackButton";
import { useViewport } from "../../../hooks/useViewport";
import { useOrderingLogout } from "./useOrderingLogout";
import BrandLogo from "../../../components/BrandLogo";

interface OrderingTopBarProps {
  /** Връщане една стъпка назад В модула (продукти → маси). Ако е подаден,
      стрелката вика него вместо да излиза към модулите — така от продуктите
      първото натискане връща при масите, а второто (вече без onBack) излиза. */
  onBack?: () => void;
  /** До заглавието — превключвателят Списък | Схема на масите (и микрофонът
      в режим „Схема"); само на десктоп, родителят решава. */
  viewSwitch?: ReactNode;
}

export default function OrderingTopBar({
  onBack,
  viewSwitch,
}: OrderingTopBarProps) {
  const { t } = useTranslation();
  const { isNarrow } = useViewport();
  const navigate = useNavigate();

  const names = useAppSelector((s) => s.auth.names);
  const username = useAppSelector((s) => s.auth.username);
  const access = useAppSelector((s) => s.auth.access);
  const roles = useAppSelector((s) => s.auth.roles);
  // Стрелката се показва при няколко роли (изход към модулите) И винаги в
  // продуктовия изглед (onBack) — там тя е единственият „назад" на телефон
  // след премахването на мобилния хедър (0.0.117)
  const showBack = roles.length > 1 || !!onBack;
  const displayName = names || username || "";
  // Изходът е общ с футъра на продуктовия изглед (useOrderingLogout)
  const { handleLogout, logoutModal } = useOrderingLogout();

  return (
    <>
      <header
        className={`flex shrink-0 items-center justify-between gap-2.5 border-b border-pos-line2 bg-pos-card font-pos-sans ${
          isNarrow ? "min-h-12 px-4 py-2.5" : "min-h-[60px] px-7 py-[11px]"
        } ${
          // В тестов режим — топъл отблясък вдясно (иначе — гладък фон)
          access
            ? "bg-[radial-gradient(ellipse_40%_120%_at_100%_50%,var(--color-pos-ready-bg)_0%,transparent_70%)]"
            : ""
        }`}
      >
        <div className="flex min-w-0 flex-1 items-center gap-2.5 leading-none">
          {showBack && <BackButton onClick={onBack ?? (() => navigate("/"))} />}
          <BrandLogo className={isNarrow ? "size-[26px]" : "size-9"} />
          {/* На телефон „UnrealSoft" се крие — остава само логото,
              за да има място за името до профил иконата. */}
          {!isNarrow && (
            <div className="min-w-0 leading-none">
              <h1
                className={`m-0 truncate text-[18px] font-bold tracking-[-0.3px] transition-colors duration-[160ms] ${
                  access ? "text-pos-accent" : "text-pos-ink"
                }`}
              >
                {t("common.appName")}
              </h1>
              {/* leading-[1.2] вместо наследеното 1: с overflow:hidden
                  кирилските descender-и (р/у/д) иначе се режат */}
              <div className="mt-1 flex items-center gap-1.5 truncate text-[12px] leading-[1.2] text-pos-soft">
                <TagOutlined className="text-[11px] !text-pos-accent-ink" />
                <span className="font-semibold text-pos-ink">
                  {t("modules.ordering")}
                </span>
              </div>
            </div>
          )}
          {viewSwitch && (
            <div className="ml-4 flex shrink-0 items-center gap-2">
              {viewSwitch}
            </div>
          )}
        </div>

        <div className={`flex shrink-0 items-center ${isNarrow ? "gap-1" : "gap-2"}`}>
          {/* ProfileDropdown сам решава: общ терминал със служител → име + директен
              бутон „Изход" (връщане към пин); личен акаунт → аватар с меню. */}
          <ProfileDropdown
            displayName={displayName}
            username={username ?? ""}
            access={access}
            isNarrow={isNarrow}
            onLogout={handleLogout}
          />
        </div>
      </header>

      {logoutModal}
    </>
  );
}
